import { useRef, useEffect, useId, useState } from 'react'
import { DotLottieCommonPlayer, DotLottiePlayer, PlayerEvents } from '@dotlottie/react-player'
import tw from 'twin.macro'

import { useIntersectionObserver } from 'usehooks-ts'
import { graphql } from 'gatsby'
import usePrevious from '@/utils/usePrevious'

export const fragment = graphql`
  fragment ComponentLottie on ContentfulComponentLottie {
    __typename
    contentful_id
    lottieFile {
      file {
        url
      }
    }
    autoplayType
  }
`

export type LottieProps = {
  className?: string
  autoplayType?: 'loop' | 'visible' | 'click' | 'hover' | 'none'
  animationData?: any
  lottieFile?: {
    file: {
      url: string
    }
  }
  /**
   * Only show a specific frame. For reduced motion.
   */
  onlyShowFrame?: number | 'last'
  onEvent?: (event: PlayerEvents) => void
  playerRef?: React.RefObject<DotLottieCommonPlayer>
  activeAnimationId?: string
}

export default function Lottie({
  className,
  animationData,
  lottieFile,
  autoplayType: _autoplayType = 'loop',
  onlyShowFrame,
  onEvent,
  playerRef,
  activeAnimationId,
}: LottieProps) {
  const id = useId()
  const lottieId = `lottie-${id.replace(/:/g, '')}`
  const ref = useRef<DotLottieCommonPlayer>(null)
  const lottieRef = playerRef || ref
  const [isLoaded, setIsLoaded] = useState(false)
  const [playQueued, setPlayQueued] = useState(false)
  const src = animationData || lottieFile?.file.url
  const prevSrc = usePrevious(src)

  const { entry, ref: containerRef } = useIntersectionObserver({ threshold: 0.5, freezeOnceVisible: true })
  const isVisible = !!entry?.isIntersecting

  const autoplayType = _autoplayType.toLowerCase()
  const autoplay = autoplayType !== 'none' && (autoplayType === 'visible' || autoplayType === 'loop')
  const loop = autoplayType === 'loop'

  const play = () => {
    if (typeof onlyShowFrame === 'number') {
      lottieRef.current?.playSegments([onlyShowFrame, onlyShowFrame], true)
    } else if (onlyShowFrame === 'last') {
      const duration = lottieRef.current?.totalFrames || 0
      lottieRef.current?.seek(duration)
    } else {
      lottieRef.current?.play()
    }
  }

  const queuePlay = () => {
    if (isLoaded) {
      play()
    } else {
      setPlayQueued(true)
    }
  }

  useEffect(() => {
    if (src && src !== prevSrc) {
      lottieRef.current?.stop()
      setIsLoaded(false)
      if (autoplay && isVisible) {
        setPlayQueued(true)
      }
    }
  }, [src])

  useEffect(() => {
    if (playQueued && isLoaded) {
      play()
      setPlayQueued(false)
    }
  }, [playQueued, isLoaded])

  useEffect(() => {
    if (autoplay && isVisible) {
      queuePlay()
    }
  }, [autoplay, isVisible])

  return (
    <div
      ref={containerRef}
      className={className}
      onClick={autoplayType === 'click' ? queuePlay : undefined}
      onKeyUp={
        autoplayType === 'click'
          ? (e) => {
              if (e.key === 'Enter') queuePlay()
            }
          : undefined
      }
      css={[autoplayType === 'click' && tw`cursor-pointer`]}
    >
      <DotLottiePlayer
        id={lottieId}
        ref={lottieRef}
        src={src}
        autoplay={false}
        loop={loop}
        hover={autoplayType === 'hover'}
        style={{ width: '100%', height: '100%' }}
        activeAnimationId={activeAnimationId}
        onEvent={(event) => {
          switch (event) {
            case PlayerEvents.Ready:
              setIsLoaded(true)
              break
            default:
          }
          onEvent?.(event)
        }}
      />
    </div>
  )
}
